import { useReactiveVar } from '@apollo/client';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { AuthenticationState, AuthErrorType, NO_PROVIDER_EMAIL_ERROR, SERVICE_BUSY_ERROR } from '@neo.tax/shared';

const getAuthErrorMessage = (error: AuthErrorType): string => {
  switch (error) {
    case AuthErrorType.INTUIT_EMAIL_NOT_VERIFIED:
      return 'Please verify your email address on Intuit.';
    case AuthErrorType.MISSING_EMAIL_IN_LOGIN_LINK:
      return 'Could not log in with the email link. Please try again.';
    case AuthErrorType.LOGIN_LINK_ERROR:
      return 'The link is invalid. This can happen if the code is malformed, expired, or has already been used.';
    // Shared with the copy thrown to callers that render the message themselves, so the same failure
    // never reaches a user worded two different ways depending on which button they pressed.
    case AuthErrorType.SERVICE_BUSY:
      return SERVICE_BUSY_ERROR;
    case AuthErrorType.SERVICE_BUSY_LOGIN_LINK:
      return 'Our system is taking longer than usual to respond. Please open the sign-in link from your email again in a moment.';
    case AuthErrorType.ACCOUNT_CHECK_FAILED:
      return 'We couldn’t finish checking your account. Please try signing in again.';
    case AuthErrorType.NO_PROVIDER_EMAIL:
      return NO_PROVIDER_EMAIL_ERROR;
    case AuthErrorType.ACCOUNT_EXISTS_DIFFERENT_CREDENTIAL:
      return 'An account already exists for this email address using a different sign-in method. Please sign in the way you did originally, or contact Neo.Tax support if that no longer works.';
    case AuthErrorType.ENTERPRISE_SSO_REQUIRED:
      return 'Your administrator has turned on single sign-on for your organization, so this account can only sign in that way. If you previously signed in with a password or another method, contact Neo.Tax support to have those removed.';
    case AuthErrorType.SSO_PROVIDER_MISMATCH:
      return 'Your organization uses a different sign-in provider for Neo.Tax. Please sign in with your organization’s approved provider, or contact your administrator.';
    case AuthErrorType.UNKNOWN:
      return 'Something went wrong. Please try again.';
  }
};

export const useAuthErrorMessage = (): [string | null, Dispatch<SetStateAction<string | null>>] => {
  const { errorType, isAuthenticating } = useReactiveVar(AuthenticationState.authenticationStateVar);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!errorType) {
      return;
    }
    setErrorMessage(getAuthErrorMessage(errorType));
    // Take the reason rather than read it. It belongs to the attempt that just failed, and every auth
    // page mounts this hook — so leaving it in shared state replays the message on the next page the
    // user opens, where nothing was attempted and nothing went wrong.
    AuthenticationState.updateAuthErrorType(null);
  }, [errorType]);

  // A sign-in now under way makes the previous attempt's advice stale, whichever route set it — the
  // shared reason above, or a form writing its own message through the returned setter.
  useEffect(() => {
    if (isAuthenticating) {
      setErrorMessage(null);
    }
  }, [isAuthenticating]);

  return [errorMessage, setErrorMessage];
};
